import { defineStore } from 'pinia';
import type { Domain, Organisation } from '@/models';
import { DomainService, OrganisationService } from '@/services/api';
import type { AxiosRequestConfig } from 'axios';

interface DomainsState {
  domains: Domain[];
  organisations: Map<string, Organisation>;
  fullyFetchedOrganisations: Set<string>; // Track orgs fetched from dedicated endpoint (with complete data)
  isLoading: boolean;
  error: string | null;
  lastFetchTime: number | null;
  cacheDuration: number; // in milliseconds
  pendingRequests: Map<string, Promise<unknown>>;
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useDomainsStore = defineStore('domains', {
  state: (): DomainsState => ({
    domains: [],
    organisations: new Map(),
    fullyFetchedOrganisations: new Set(),
    isLoading: false,
    error: null,
    lastFetchTime: null,
    cacheDuration: CACHE_DURATION,
    pendingRequests: new Map()
  }),

  getters: {
    isCacheValid(): boolean {
      if (!this.lastFetchTime) {
        return false;
      }
      return Date.now() - this.lastFetchTime < this.cacheDuration;
    },

    allDomains(): Domain[] {
      return this.domains;
    },

    getDomainById: (state) => (domainId: string): Domain | null => {
      const findDomain = (domains: Domain[]): Domain | null => {
        for (const domain of domains) {
          // 1. Check if this domain matches the ID
          if (domain.id === domainId) {
            return domain;
          }

          // 2. Recurse into subdomains
          if (domain.subdomains?.length) {
            const found = findDomain(domain.subdomains);
            if (found) {
              return found;
            }
          }

          // 3. Check if the ID matches an organisation (returns parent domain)
          if (domain.organisations?.some((org) => org.id === domainId)) {
            return domain;
          }
        }
        return null;
      };

      return findDomain(state.domains);
    },

    getOrganisationById: (state) => (organisationId: string): Organisation | null =>
      (state.organisations.get(organisationId) || null)
  },

  actions: {
    /**
     * Fetch domains with caching support
     * @param config - Axios request config
     * @param forceRefresh - Force refresh ignoring cache
     * @returns Promise with domain data
     */
    async fetchDomains(
      config?: AxiosRequestConfig,
      forceRefresh = false
    ): Promise<Domain[]> {
      // Return cached data if valid and not forcing refresh
      if (!forceRefresh && this.isCacheValid && this.domains.length > 0) {
        return this.domains;
      }

      // Check if there's already a pending request
      const requestKey = 'fetchDomains';
      const pendingRequest = this.pendingRequests.get(requestKey);
      if (pendingRequest) {
        return pendingRequest as Promise<Domain[]>;
      }

      // Create new request
      const domainService = new DomainService();
      const requestPromise = (async (): Promise<Domain[]> => {
        try {
          this.isLoading = true;
          this.error = null;

          const response = await domainService.index({
            ...config,
            params: {
              mode: 'tree',
              ...config?.params
            }
          });

          this.domains = response.data;
          this.lastFetchTime = Date.now();

          // Update organisations map
          this.extractOrganisations(response.data);

          return this.domains;
        } catch (error) {
          this.error = error instanceof Error ? error.message : 'Failed to fetch domains';
          throw error;
        } finally {
          this.isLoading = false;
          this.pendingRequests.delete(requestKey);
        }
      })();

      this.pendingRequests.set(requestKey, requestPromise);
      return await requestPromise;
    },

    /**
     * Fetch a single organisation by ID with caching and deduplication
     * @param organisationId - Organisation ID
     * @param config - Axios request config
     * @param forceRefresh - Force refresh ignoring cache
     * @returns Promise with organisation data
     */
    async fetchOrganisation(
      organisationId: string,
      config?: AxiosRequestConfig,
      forceRefresh = false
    ): Promise<Organisation> {
      // Return cached data if available, not forcing refresh, and was fully fetched
      // (not just extracted from domains tree which has incomplete data)
      const cached = this.organisations.get(organisationId);
      const wasFullyFetched = this.fullyFetchedOrganisations.has(organisationId);
      if (!forceRefresh && cached && wasFullyFetched) {
        return cached;
      }

      // Check if there's already a pending request for this organisation
      const requestKey = `fetchOrganisation:${organisationId}`;
      const pendingRequest = this.pendingRequests.get(requestKey);
      if (pendingRequest) {
        return pendingRequest as Promise<Organisation>;
      }

      // Create new request
      const orgService = new OrganisationService();
      const requestPromise = (async () => {
        try {
          const organisation = await orgService.fetch(organisationId, {
            ...config,
            params: {
              append: 'features',
              ...config?.params
            }
          });

          this.organisations.set(organisationId, organisation);
          this.fullyFetchedOrganisations.add(organisationId);
          return organisation;
        } finally {
          this.pendingRequests.delete(requestKey);
        }
      })();

      this.pendingRequests.set(requestKey, requestPromise);
      return await requestPromise;
    },

    /**
     * Fetch a single domain by ID with caching and deduplication
     * @param domainId - Domain ID
     * @param config - Axios request config
     * @param forceRefresh - Force refresh ignoring cache
     * @returns Promise with domain data
     */
    async fetchDomain(
      domainId: string,
      config?: AxiosRequestConfig,
      forceRefresh = false
    ): Promise<Domain> {
      // Check cache first
      if (!forceRefresh) {
        const cached = this.getDomainById(domainId);
        if (cached) {
          return cached;
        }
      }

      // Check if there's already a pending request for this domain
      const requestKey = `fetchDomain:${domainId}`;
      const pendingRequest = this.pendingRequests.get(requestKey);
      if (pendingRequest) {
        return pendingRequest as Promise<Domain>;
      }

      // Create new request
      const domainService = new DomainService();
      const requestPromise = (async () => {
        try {
          const domain = await domainService.fetch(domainId, {
            ...config,
            params: {
              append: 'features',
              ...config?.params
            }
          });

          // Update cache with fetched domain
          this.updateDomainInCache(domain);
          return domain;
        } finally {
          this.pendingRequests.delete(requestKey);
        }
      })();

      this.pendingRequests.set(requestKey, requestPromise);
      return await requestPromise;
    },

    /**
     * Extract and cache all organisations from domain tree
     * Note: Organisations from the domains tree are cached but NOT marked as fully fetched
     * because they may not have all the relationships that the dedicated organisation endpoint provides.
     * This ensures that when components need complete organisation data, they will fetch it from the API.
     */
    extractOrganisations(domains: Domain[]): void {
      const extractFromDomain = (domain: Domain) => {
        if (domain.organisations) {
          domain.organisations.forEach((org) => {
            // Only cache if not already marked as fully fetched from a dedicated fetch
            if (!this.fullyFetchedOrganisations.has(org.id)) {
              this.organisations.set(org.id, org);
            }
          });
        }
        if (domain.subdomains) {
          domain.subdomains.forEach((subdomain) => {
            extractFromDomain(subdomain);
          });
        }
      };

      domains.forEach((domain) => extractFromDomain(domain));
    },

    /**
     * Update a specific domain in the cache
     * Preserves existing subdomains and organisations_count to prevent tree structure/metadata loss
     * when updating with single-domain API responses that don't include tree-specific data
     */
    updateDomainInCache(updatedDomain: Domain): void {
      const updateInTree = (domains: Domain[]): Domain[] =>
        domains.map((domain) => {
          if (domain.id === updatedDomain.id) {
            return {
              ...updatedDomain,
              subdomains: domain.subdomains ?? updatedDomain.subdomains,
              organisations_count: domain.organisations_count ?? updatedDomain.organisations_count
            };
          }
          if (domain.subdomains) {
            domain.subdomains = updateInTree(domain.subdomains);
          }
          return domain;
        });

      this.domains = updateInTree(this.domains);
      this.extractOrganisations(this.domains);
    },

    /**
     * Update a specific organisation in the cache with complete data
     * This fully replaces the cached organisation to ensure data consistency
     */
    updateOrganisationInCache(organisationId: string, organisation: Organisation): void {
      this.organisations.set(organisationId, organisation);
      this.fullyFetchedOrganisations.add(organisationId);
    },

    /**
     * Invalidate a specific organisation's cache, forcing a fresh fetch next time
     */
    invalidateOrganisation(organisationId: string): void {
      this.fullyFetchedOrganisations.delete(organisationId);
    },

    /**
     * Invalidate cache and clear data
     */
    invalidateCache(): void {
      this.lastFetchTime = null;
      this.domains = [];
      this.organisations.clear();
      this.fullyFetchedOrganisations.clear();
      this.error = null;
    },

    /**
     * Clear all pending requests
     */
    clearPendingRequests(): void {
      this.pendingRequests.clear();
    },

    /**
     * Set cache duration
     */
    setCacheDuration(duration: number): void {
      this.cacheDuration = duration;
    }
  }
});
